import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { Viewer } from '@photo-sphere-viewer/core';
import '@photo-sphere-viewer/core/index.css';

interface Props {
  url: string;
  accessId: string;
  viewSwitchHandler: () => void;
}

const switchSvg = `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
<path d="M20 0H2C0.9 0 0 0.9 0 2V14C0 15.1 0.9 16 2 16H20C21.1 16 22 15.1 22 14V2C22 0.9 21.1 0 20 0ZM20 14H2V2H20V14ZM13.5 7L10 11.51L7.5 8.5L4 13H18L13.5 7Z" fill="currentColor"/>
</svg>`;

const screenshotSvg = `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
<mask id="mask0_148_192" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
<rect width="24" height="24" fill="#D9D9D9"/>
</mask>
<g mask="url(#mask0_148_192)">
<path d="M15 16H19V12H17.5V14.5H15V16ZM5 10H6.5V7.5H9V6H5V10ZM8 21V19H4C3.45 19 2.97917 18.8042 2.5875 18.4125C2.19583 18.0208 2 17.55 2 17V5C2 4.45 2.19583 3.97917 2.5875 3.5875C2.97917 3.19583 3.45 3 4 3H20C20.55 3 21.0208 3.19583 21.4125 3.5875C21.8042 3.97917 22 4.45 22 5V17C22 17.55 21.8042 18.0208 21.4125 18.4125C21.0208 18.8042 20.55 19 20 19H16V21H8ZM4 17H20V5H4V17Z" fill="currentColor"/>
</g>
</svg>
`;

const resetSvg = `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
<mask id="mask0_148_189" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
<rect width="24" height="24" fill="#D9D9D9"/>
</mask>
<g mask="url(#mask0_148_189)">
<path d="M7.5 16.5L14.5 14.5L16.5 7.5L9.5 9.5L7.5 16.5ZM12 13.5C11.5833 13.5 11.2292 13.3542 10.9375 13.0625C10.6458 12.7708 10.5 12.4167 10.5 12C10.5 11.5833 10.6458 11.2292 10.9375 10.9375C11.2292 10.6458 11.5833 10.5 12 10.5C12.4167 10.5 12.7708 10.6458 13.0625 10.9375C13.3542 11.2292 13.5 11.5833 13.5 12C13.5 12.4167 13.3542 12.7708 13.0625 13.0625C12.7708 13.3542 12.4167 13.5 12 13.5ZM12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C14.2167 20 16.1042 19.2208 17.6625 17.6625C19.2208 16.1042 20 14.2167 20 12C20 9.78333 19.2208 7.89583 17.6625 6.3375C16.1042 4.77917 14.2167 4 12 4C9.78333 4 7.89583 4.77917 6.3375 6.3375C4.77917 7.89583 4 9.78333 4 12C4 14.2167 4.77917 16.1042 6.3375 17.6625C7.89583 19.2208 9.78333 20 12 20Z" fill="currentColor"/>
</g>
</svg>
`;

export const switchViewButton = {
  id: 'view-switch',
  content: switchSvg,
  title: 'View in Standard Mode',
};

export const screenshotButton = {
  id: 'screenshot',
  content: screenshotSvg,
  title: 'Screenshot',
  onClick: (panoViewer: any) => {
    panoViewer.addEventListener('render', () => {
      const link = document.createElement('a');
      // eslint-disable-next-line no-alert
      const filename = window.prompt('Please enter a filename', 'my_screenshot');
      if (filename) {
        link.download = `${filename}.png`;
        // @ts-ignore
        link.href = panoViewer.renderer.renderer.domElement.toDataURL();
        link.click();
      }
    }, { once: true });
    panoViewer.needsUpdate();
  },
};

export const resetButton = {
  id: 'reset',
  content: resetSvg,
  title: 'Reset Orientation',
  onClick: (panoViewer: any) => {
    panoViewer.rotate({ pitch: 0, yaw: 0 });
    panoViewer.zoom(50);
  },
};

const PanoramaPhotoViewer = ({ url, accessId, viewSwitchHandler }: Props) => {
  useEffect(() => {
    // @ts-ignore
    switchViewButton.onClick = () => viewSwitchHandler();
    const viewer = new Viewer({
      container: `#panorama${accessId}`,
      panorama: url,
    });

    viewer.navbar.setButtons([
      switchViewButton,
      'zoom',
      'move',
      resetButton,
      screenshotButton,
      'fullscreen',
    ]);

    const handleButtonDisplay = (width: number): void => {
      const moveUp = viewer.navbar.getButton('moveUp');
      const moveDown = viewer.navbar.getButton('moveDown');
      const moveRight = viewer.navbar.getButton('moveRight');
      const moveLeft = viewer.navbar.getButton('moveLeft');
      const buttonGroup = [moveUp, moveDown, moveRight, moveLeft];

      buttonGroup.forEach((button: any) => {
        if (width < 450 && button.isVisible()) {
          button.hide();
        }

        if (width >= 450 && !button.isVisible()) {
          button.show();
        }
      });
    };
    // determine button display on ready as viewer is initialized at a specific screen size
    viewer.addEventListener('ready', () => {
      const { width } = viewer.getSize();
      if (width) {
        handleButtonDisplay(width);
      }
    });

    // determine button display as user manually adjusts screen size
    viewer.addEventListener('size-updated', (e: any) => {
      const width = e?.size?.width;
      if (width) {
        handleButtonDisplay(width);
      }
    });
  }, []);

  return (
    <Box id={`panorama${accessId}`} data-testid="panorama-viewer" sx={{ width: '42vw', height: '68vh' }} />
  );
};

export default PanoramaPhotoViewer;
